import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, CheckIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
export function CookieBanner() {
  const [isVisible, setIsVisible] = useState(false);
  const {
    t,
    getPath,
    navigate
  } = useLanguage();
  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      setTimeout(() => setIsVisible(true), 1000);
    }
  }, []);
  const handleAccept = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    setIsVisible(false);
  };
  const declineCookies = () => {
    localStorage.setItem('cookieConsent', 'declined');
    setIsVisible(false);
  };
  const handleInternalLink = (e: React.MouseEvent<HTMLAnchorElement>, path: string) => {
    e.preventDefault();
    navigate(path);
  };
  if (!isVisible) return null;
  return <AnimatePresence data-id="element-94" data-mp="kdj7sse2xu76">
      {isVisible && <motion.div initial={{
      y: 100,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} exit={{
      y: 100,
      opacity: 0
    }} transition={{
      duration: 0.4,
      ease: 'easeOut'
    }} className="fixed bottom-4 left-4 right-4 sm:bottom-6 sm:left-6 sm:right-6 sm:max-w-4xl sm:mx-auto z-50" data-id="element-95" data-mp="kektrrwmw13o">
        
          <div className="rounded-2xl border shadow-2xl backdrop-blur-xl" style={{
        backgroundColor: 'var(--bg-secondary)',
        borderColor: 'var(--border)'
      }} data-id="element-96" data-mp="ttfvhuia2llp">
          
            {/* Mobile: Compact vertical layout */}
            <div className="sm:hidden p-5" data-id="element-97" data-mp="1lklo7pvo5h0y">
              <div className="flex items-start justify-between mb-4" data-id="element-98" data-mp="17lm8x8u1qehg">
                <h3 className="text-base font-semibold" style={{
              color: 'var(--text-primary)'
            }} data-id="element-99" data-mp="17ngixs3s4m6r">
                
                  🍪 {t('cookie.title')}
                </h3>
                <button onClick={declineCookies} className="p-1 rounded-lg transition-colors" style={{
              color: 'var(--text-muted)'
            }} aria-label="Close" data-id="element-100" data-mp="3sdo09jhqnift">
                
                  <XIcon className="w-4 h-4" data-id="element-101" data-mp="3dany2olr6nj4" />
                </button>
              </div>

              <p className="text-xs sm:text-sm mb-4 sm:mb-0" style={{
            color: 'var(--text-secondary)'
          }} data-id="element-102" data-mp="397rlpfmt3nnm">
              
                {t('cookie.description')}{' '}
                <a href={getPath('privacy')} onClick={e => handleInternalLink(e, 'privacy')} className="underline hover:opacity-80 transition-opacity" style={{
              color: 'var(--text-primary)'
            }} data-id="element-103" data-mp="2zgwppjjrd9oz">
                
                  {t('cookie.learnMore')}
                </a>
              </p>

              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 shrink-0" data-id="element-104" data-mp="2ljhxy8618ptu">
                <motion.button whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} onClick={handleAccept} className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-xs transition-all" style={{
              backgroundColor: 'var(--accent)',
              color: '#FFFFFF'
            }} data-id="element-105" data-mp="2gk59xk3fdcpo">
                
                  <CheckIcon className="w-3 h-3" data-id="element-106" data-mp="r7yxv3giwpzo" />
                  {t('cookie.accept')}
                </motion.button>

                <motion.button whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} onClick={declineCookies} className="px-4 py-2.5 rounded-lg font-semibold text-xs border transition-all" style={{
              borderColor: 'var(--border)',
              backgroundColor: 'var(--bg-tertiary)',
              color: 'var(--text-primary)'
            }} data-id="element-107" data-mp="w2mc89htc40j">
                
                  {t('cookie.decline')}
                </motion.button>
              </div>
            </div>

            {/* Desktop: Horizontal stretched layout */}
            <div className="hidden sm:flex items-center gap-6 p-6" data-id="element-108" data-mp="1or0c6usgpaeq">
              <div className="flex-1" data-id="element-109" data-mp="1sp7rrmb6w5oc">
                <h3 className="text-lg font-semibold mb-2" style={{
              color: 'var(--text-primary)'
            }} data-id="element-110" data-mp="1ssdi14vk1dln">
                
                  🍪 {t('cookie.title')}
                </h3>
                <p className="text-sm leading-relaxed mb-2" style={{
              color: 'var(--text-secondary)'
            }} data-id="element-111" data-mp="217jj6qyigr2p">
                
                  {t('cookie.description')}
                </p>
                <a href={getPath('privacy')} className="text-sm font-medium inline-block hover:underline" style={{
              color: 'var(--accent)'
            }} data-id="element-112" data-mp="2a7y5ntn7oc7s">
                
                  {t('cookie.learnMore')} →
                </a>
              </div>

              <div className="flex items-center gap-3" data-id="element-113" data-mp="2k03w2yxtelkv">
                <motion.button whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} onClick={handleAccept} className="flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-sm transition-all whitespace-nowrap" style={{
              backgroundColor: 'var(--accent)',
              color: '#FFFFFF'
            }} data-id="element-114" data-mp="2ezzef5l67kt2">
                
                  <CheckIcon className="w-4 h-4" data-id="element-115" data-mp="3ckr8e1qqon4n" />
                  {t('cookie.accept')}
                </motion.button>

                <motion.button whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} onClick={declineCookies} className="px-6 py-3 rounded-lg font-semibold text-sm border transition-all whitespace-nowrap" style={{
              borderColor: 'var(--border)',
              backgroundColor: 'var(--bg-tertiary)',
              color: 'var(--text-primary)'
            }} data-id="element-116" data-mp="2yk81ixy0fn42">
                
                  {t('cookie.decline')}
                </motion.button>

                <button onClick={declineCookies} className="p-2 rounded-lg transition-colors" style={{
              color: 'var(--text-muted)'
            }} aria-label="Close" data-id="element-117" data-mp="2jw3fbjzr0tgj">
                
                  <XIcon className="w-5 h-5" data-id="element-118" data-mp="2audpgilim2kv" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>}
    </AnimatePresence>;
}